import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { goalDataTypeEnums, renderDate } from 'utils';
import { goalTypes } from 'fragments';

/**
 * Session Data Table — authoring catalog.
 *
 * A Session Data Table is NOT a free-form table. Its columns bind to fields of
 * the session's collected data (one row per goal/target pair that was collected
 * during the appointment's clinical session), so the catalog is closed:
 * template authors pick which of these fields to show, never invent new ones.
 *
 * The field strings and their labels are the stored wire contract, authored
 * api-side in `package/src/form-builder/constants/sessionDataTable.constants.ts`
 * — the api resolver keys each resolved row object by the same strings, and the
 * members portal and PDF renderer read them the same way. They are re-declared
 * here rather than imported from `@therapylake/package`, which is a backend
 * build the client cannot depend on (see `signatureFill.constants.js` for the
 * full reason). Keep the values in step with that file.
 */

/** Summary-row fields — one row per goal/target pair collected in the session. */
export const sessionDataColumnEnums = {
    GOAL: 'GOAL',
    TARGET: 'TARGET',
    DATA_METHOD: 'DATA_METHOD',
    RESULT: 'RESULT',
    TRIALS: 'TRIALS',
    GOAL_TYPE: 'GOAL_TYPE',
    SESSION_DATE: 'SESSION_DATE',
};

/** Per-trial breakdown fields, nested under each summary row. */
export const sessionDataTrialColumnEnums = {
    TRIAL: 'TRIAL',
    VALUE: 'VALUE',
    PROMPT: 'PROMPT',
    START_TIME: 'START_TIME',
    END_TIME: 'END_TIME',
    TIME_COLLECTED: 'TIME_COLLECTED',
};

/**
 * Where a summary column's cells come from. AUTO binds to a session-data field
 * the api resolves; MANUAL is filled in by staff once per row, and its answer
 * lives on the row under `SESSION_DATA_MANUAL_KEY`, keyed by the column id. A
 * column without `kind` is AUTO.
 */
export const sessionDataColumnKindEnums = {
    AUTO: 'AUTO',
    MANUAL: 'MANUAL',
};

/** How staff fill a MANUAL column's cells. */
export const sessionDataManualInputEnums = {
    TEXT: 'TEXT',
    NUMBER: 'NUMBER',
    /** Stored as the date input's `YYYY-MM-DD`; formatted for display. */
    DATE: 'DATE',
    DROPDOWN: 'DROPDOWN',
};

export const SESSION_DATA_MANUAL_KEY = 'manual';

export const DEFAULT_SESSION_DATA_MANUAL_COLUMN_NAME = 'Staff comments';

/** Why a resolved block has no rows. Stamped as `sessionDataState`. */
export const sessionDataStateEnums = {
    /** Rows resolved. */
    OK: 'OK',
    /** The appointment has no clinical session at all. */
    NO_SESSION: 'NO_SESSION',
    /** A session exists but has not been completed — data is not final yet. */
    NOT_COMPLETED: 'NOT_COMPLETED',
    /** Session completed, but nothing was collected. */
    NO_DATA: 'NO_DATA',
};

/** Column headers, as shown in the authoring catalog and on every render surface. */
const SESSION_DATA_COLUMN_NAMES = {
    [sessionDataColumnEnums.GOAL]: 'Goal',
    [sessionDataColumnEnums.TARGET]: 'Target',
    [sessionDataColumnEnums.DATA_METHOD]: 'Data Method',
    [sessionDataColumnEnums.RESULT]: 'Result',
    [sessionDataColumnEnums.TRIALS]: 'Trials',
    [sessionDataColumnEnums.GOAL_TYPE]: 'Goal Type',
    [sessionDataColumnEnums.SESSION_DATE]: 'Session Date',
};

const SESSION_DATA_TRIAL_COLUMN_NAMES = {
    [sessionDataTrialColumnEnums.TRIAL]: 'Trial',
    [sessionDataTrialColumnEnums.VALUE]: 'Value',
    [sessionDataTrialColumnEnums.PROMPT]: 'Prompt',
    [sessionDataTrialColumnEnums.START_TIME]: 'Start Time',
    [sessionDataTrialColumnEnums.END_TIME]: 'End Time',
    [sessionDataTrialColumnEnums.TIME_COLLECTED]: 'Time Collected',
};

/**
 * The trial VALUE column is named after what the goal measures — the live
 * session's trial-history table names it "Frequency" for a frequency goal,
 * "Latency" for a latency goal, and so on. Data types not listed here keep the
 * generic "Value" heading.
 */
const SESSION_DATA_TRIAL_VALUE_HEADERS = {
    [goalDataTypeEnums.FREQUENCY]: 'Frequency',
    [goalDataTypeEnums.RATING]: 'Rate',
    [goalDataTypeEnums.RATE]: 'Rate',
    [goalDataTypeEnums.LATENCY]: 'Latency',
    [goalDataTypeEnums.DURATION]: 'Duration',
    [goalDataTypeEnums.SCORE]: 'Score',
    [goalDataTypeEnums.IRT]: 'IRT',
};

/**
 * Why the table is empty, in the reader's words.
 *
 * `OK` has no message — that state always has rows. An unknown or absent state
 * must NOT borrow the NOT_COMPLETED wording: a block the api never stamped would
 * then assert something false about the clinical session.
 */
const SESSION_DATA_EMPTY_MESSAGES = {
    [sessionDataStateEnums.NO_SESSION]: 'No session data — this appointment has no clinical session.',
    [sessionDataStateEnums.NOT_COMPLETED]: 'The session is not completed yet — data will appear once it is.',
    [sessionDataStateEnums.NO_DATA]: 'No data was collected during this session.',
};

/** Shown when a block carries no resolvable state at all. */
export const SESSION_DATA_UNKNOWN_MESSAGE = 'No session data to show.';

export const getSessionDataColumnName = (field) => SESSION_DATA_COLUMN_NAMES[field] ?? '';

export const getSessionDataTrialColumnName = (field, dataType) => {
    if (field === sessionDataTrialColumnEnums.VALUE) {
        return SESSION_DATA_TRIAL_VALUE_HEADERS[dataType] ?? SESSION_DATA_TRIAL_COLUMN_NAMES[field];
    }
    return SESSION_DATA_TRIAL_COLUMN_NAMES[field] ?? '';
};

export const getSessionDataEmptyMessage = (state) =>
    SESSION_DATA_EMPTY_MESSAGES[state] ?? SESSION_DATA_UNKNOWN_MESSAGE;

/** Pickable fields for the summary table, in catalog order. */
export const sessionDataColumnList = Object.values(sessionDataColumnEnums).map((field) => ({
    id: field,
    field,
    name: getSessionDataColumnName(field),
}));

/**
 * Pickable fields for the per-trial breakdown. Mirrors the live session trial
 * history table (fragments/clinicalFragments/session/core/trialHistory), which
 * shows the same `tSummary.trials[]` data.
 */
export const sessionDataTrialColumnList = Object.values(sessionDataTrialColumnEnums).map((field) => ({
    id: field,
    field,
    name: getSessionDataTrialColumnName(field),
}));

/**
 * Goal types whose rows a block includes (`properties.sessionDataTable.goalTypes`).
 * Absent or empty means every type — that is how every block authored before
 * the option existed reads, and the api treats it the same way.
 */
export const getSessionDataGoalTypes = (config) =>
    config?.goalTypes?.length ? config.goalTypes : goalTypes.map((opt) => opt.id);

/** Columns shown on a freshly added block, in order. */
const DEFAULT_COLUMN_FIELDS = [
    sessionDataColumnEnums.GOAL,
    sessionDataColumnEnums.TARGET,
    sessionDataColumnEnums.DATA_METHOD,
    sessionDataColumnEnums.RESULT,
];

const DEFAULT_TRIAL_COLUMN_FIELDS = [
    sessionDataTrialColumnEnums.TRIAL,
    sessionDataTrialColumnEnums.PROMPT,
    sessionDataTrialColumnEnums.TIME_COLLECTED,
];

/**
 * Columns carry a generated `id` separate from `field` so that a column can be
 * reordered, re-pointed at another field, or duplicated without its identity
 * changing — the same contract the free-form TABLE attribute's `tableHead` uses,
 * which keeps resolved row objects keyed stably.
 */
export const buildSessionDataColumns = (fields = DEFAULT_COLUMN_FIELDS) => fields.map((field) => ({ id: uuidv4(), field }));

export const buildSessionDataTrialColumns = (fields = DEFAULT_TRIAL_COLUMN_FIELDS) =>
    fields.map((field) => ({ id: uuidv4(), field }));

/** A fresh staff-filled column, as the "Manual column" button adds it. */
export const buildManualSessionDataColumn = () => ({
    id: uuidv4(),
    kind: sessionDataColumnKindEnums.MANUAL,
    name: DEFAULT_SESSION_DATA_MANUAL_COLUMN_NAME,
    inputType: sessionDataManualInputEnums.TEXT,
    options: [],
    required: false,
});

export const sessionDataManualInputList = [
    { id: sessionDataManualInputEnums.TEXT, name: 'Text' },
    { id: sessionDataManualInputEnums.NUMBER, name: 'Number' },
    { id: sessionDataManualInputEnums.DATE, name: 'Date' },
    { id: sessionDataManualInputEnums.DROPDOWN, name: 'Dropdown' },
];

/**
 * Date/time cells arrive as raw ISO strings — the api has no viewer timezone, so
 * anything needing one is formatted here. Every other field is already
 * display-ready (labels and aggregates are resolved server-side so a saved note
 * carries them into the PDF renderer).
 */
const formatTime = (value) => (value ? moment(value).local().format('h:mm A') : '--');

export const formatSessionDataCell = (field, value) => {
    if (value === null || value === undefined || value === '') return '';
    if (field === sessionDataColumnEnums.SESSION_DATE) return renderDate(value);
    return value;
};

export const formatSessionDataTrialCell = (field, value) => {
    switch (field) {
        case sessionDataTrialColumnEnums.START_TIME:
        case sessionDataTrialColumnEnums.END_TIME:
        case sessionDataTrialColumnEnums.TIME_COLLECTED:
            return formatTime(value);
        default:
            return value ?? '';
    }
};

export const isManualSessionDataColumn = (col) => col?.kind === sessionDataColumnKindEnums.MANUAL;

/** Header of a summary column: the author's name for MANUAL, the field's label for AUTO. */
export const getSessionDataColumnHeader = (col) =>
    isManualSessionDataColumn(col) ? col?.name || DEFAULT_SESSION_DATA_MANUAL_COLUMN_NAME : getSessionDataColumnName(col?.field);

/** Header of a trial column: the author's name for MANUAL, the (data-type aware) field label for AUTO. */
export const getSessionDataTrialColumnHeader = (col, dataType) =>
    isManualSessionDataColumn(col)
        ? col?.name || DEFAULT_SESSION_DATA_MANUAL_COLUMN_NAME
        : getSessionDataTrialColumnName(col?.field, dataType);

/** Staff's answer to a MANUAL column, stored on the row or trial under `manual`. */
const formatManualAnswer = (col, record) => {
    const answer = record?.[SESSION_DATA_MANUAL_KEY]?.[col.id] ?? '';
    return answer && col.inputType === sessionDataManualInputEnums.DATE ? renderDate(answer) : answer;
};

/** A summary cell for display: staff's answer for MANUAL, the formatted field for AUTO. */
export const getSessionDataCellValue = (col, row) =>
    isManualSessionDataColumn(col) ? formatManualAnswer(col, row) : formatSessionDataCell(col?.field, row?.[col?.field]);

/** A trial cell for display: staff's answer for MANUAL, the formatted field for AUTO. */
export const getSessionDataTrialCellValue = (col, trial) =>
    isManualSessionDataColumn(col) ? formatManualAnswer(col, trial) : formatSessionDataTrialCell(col?.field, trial?.[col?.field]);
